<template>
  <div v-if="detailPage.selectedInvestmentOption" class="mt-4">
    <div class="optionName mt-3 pl-3 w-100 d-block">{{ detailPage.selectedInvestmentOption.name }}</div>
    <loading
      v-if="detailPage.loadingInvestmentOption"
      :active="true"
      :is-full-page="false"
      color="blue"
      loader="spinner"
    ></loading>
    <template v-else-if="response">
      <field-catalog-section
        v-if="kiidDetailSection"
        :section-key="kiidDetailSection.sectionKey"
        :fields="kiidDetailSection.fields"
      ></field-catalog-section>
      <b-tabs v-else v-model="activeSecondaryTabIndex" class="fairmat-secondary-tabs mt-4">
        <b-tab :title="$t('productDetails.kpiKid')" lazy>
          <b-container class="text-right mt-3">
            <b-row>
              <b-col cols="5" offset="7" class="pointer fairmatPrimary" @click="pushToRoute('marketComparison')">
                <span v-t="'productDetails.toMarketComparer'" data-test="marketCompPerInvestmentOption"></span>
                <i class="ml-2 fas fa-chevron-right"></i>
              </b-col>
            </b-row>
          </b-container>
          <detail-section v-for="section in kpiKidSections" :key="section.sectionKey" :section="section"></detail-section>
        </b-tab>
        <b-tab v-if="targetMarketSection" :title="$t('productDetails.targetMarket')" lazy>
          <detail-section :section="targetMarketSection"></detail-section>
        </b-tab>
        <b-tab v-if="sustainabilitySection" :title="$t('sustainability.title')" lazy>
          <detail-section :section="sustainabilitySection"></detail-section>
        </b-tab>
        <!-- Per-option Benchmarking — never shown for a KIID target (this whole v-else branch already
        excludes KIID responses, see kiidDetailSection above). Shares onChangeScoringProfile/
        createNewScoringProfile with the product-level tab (ProductDetails.vue, via the detailPage
        inject) — a single shared scoring-profile selection for both <benchmarking> instances. -->
        <b-tab v-if="canViewBenchmarking" :title="$t('productDetails.benchmarking')" lazy>
          <template v-if="response.benchmarkingAccess.disabledReason != null">
            <b-row class="mt-3">
              <b-col>
                <no-content
                  :icon-name="'exclamation-circle'"
                  :icon-class="['mb-3', 'fa-3x']"
                  :text="benchmarkingDisabledReasonMessage"
                ></no-content>
              </b-col>
            </b-row>
          </template>
          <template v-else>
            <benchmarking
              class="mt-3"
              :fairmat-product-score-update-key="detailPage.fairmatProductScoreKey"
              :scoring-profiles-list="detailPage.getScoringProfilesList"
              :current-profile-id="detailPage.getCurrentProfileId"
              :associated-scoring-profile="detailPage.getScoringProfileAssociated"
              :product-score="detailPage.getProductScoreInvestment"
              :loading-product-score="detailPage.getLoadingProductScore"
              :success-on-associate="detailPage.successOnAssociate"
              :is-product-in-test="detailPage.response.isProductInTest"
              @on-click-create="detailPage.createNewScoringProfile"
              @on-change-scoring-profile="detailPage.onChangeScoringProfile"
            ></benchmarking>
          </template>
        </b-tab>
      </b-tabs>
    </template>
  </div>
</template>

<script>
// The per-option drill-down panel (an in-page switch, not a distinct
// route). Mounted once by InvestmentOptionsListSection.vue, reacts to ProductDetails.vue's own
// selectedInvestmentOption/investmentOptionResponse state (read through the same detailPage inject
// every other typed section already uses — see selectInvestmentOption() there) rather than owning
// its own fetch, so a SID row click (InvestmentOptionsListSection.vue) and a future KIID/other
// entry point can all drive the same panel through one shared method.
//
// GET /api/detail-page/investmentOption/{id} returns a FLAT Sections[] (no TabKey grouping — unlike
// DetailPageResponse.Tabs, there is exactly one implicit tab here, see InvestmentOptionDetailPageResponse.cs)
// so the KpiKid/TargetMarket/Sustainability secondary-tab grouping is reconstructed here by
// SectionKey, ordered to match the seeded catalog's own section_order — the backend doesn't repeat that grouping in the
// per-option contract since it would just duplicate what the catalog already encodes.
//
// SidType (0 = SID, 1 = KIID, OpenKid.Model.SidType) is NOT part of the field-catalog projector
// system — a plain typed property on InvestmentOptionDetailPageResponse — so it serializes as a raw
// number by .NET's own default (no JsonStringEnumConverter registered anywhere in the app, same rule
// already documented in AdditionalInfoSection.vue/InvestmentOptionsProfileSection.vue). A KIID
// response only ever carries the KiidDetail section (ProductDetailPageManager.GetInvestmentOptionDetailPage's
// own SidType branch) — checking for kiidDetailSection's presence is equivalent to
// checking sidType === 1 and reads more directly off the data actually received.
//
// KiidDetail's own two Enum fields (kiidFundCategory/kiidUnderlyingType) resolve through the
// filterSelectValues.optionFundCategory/optionUnderlyingType dictionaries — see
// detailFieldFormat.js's detailFieldEnumLabel for the name→code bridge, not a gap.
import FieldCatalogSection from './FieldCatalogSection'
import Benchmarking from './Benchmarking'
import NoContent from '../Lib/NoContent'

const KPI_KID_SECTION_ORDER = ['InvestmentOptionsProfile', 'CostsProfile', 'CostsAtExpiration', 'PerformanceScenarios', 'PerformanceIndicators']

export default {
  name: 'investment-option-detail-panel',
  components: {
    FieldCatalogSection,
    Benchmarking,
    NoContent,
  },
  inject: ['detailPage'],
  // DetailSection.vue registers InvestmentOptionsListSection.vue (its own dispatch table), which
  // renders this component, which in turn needs DetailSection.vue itself to render the drilldown's
  // own sections — a genuine circular import (DetailSection → InvestmentOptionsListSection →
  // InvestmentOptionDetailPanel → DetailSection). A static top-level `import DetailSection from
  // './DetailSection'` resolves to `undefined` at this file's module-evaluation time (DetailSection.vue
  // is still mid-evaluation, being the module that started the cycle) — Vue then warns "Unknown
  // custom element: <detail-section>" at render time, silently rendering nothing wherever it's used
  // below. Fixed with Vue 2's own documented circular-component workaround (a lazy `require()` inside
  // `beforeCreate`, deferred until every module has finished loading):
  // https://v2.vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
  beforeCreate() {
    this.$options.components.DetailSection = require('./DetailSection').default
  },
  computed: {
    response() {
      return this.detailPage.investmentOptionResponse
    },
    sections() {
      return (this.response && this.response.sections) || []
    },
    kiidDetailSection() {
      return this.sections.find((s) => s.sectionKey === 'KiidDetail')
    },
    kpiKidSections() {
      return KPI_KID_SECTION_ORDER.map((key) => this.sections.find((s) => s.sectionKey === key)).filter(Boolean)
    },
    targetMarketSection() {
      return this.sections.find((s) => s.sectionKey === 'TargetMarket')
    },
    sustainabilitySection() {
      return this.sections.find((s) => s.sectionKey === 'Sustainability')
    },
    // Same two-condition gate as ProductDetails.vue's own canViewProductBenchmarking (permission
    // read once via the detailPage inject + per-option BenchmarkingAccess on this response — NOT the
    // product-level one on detailPage.response, this option can be disabled independently... in
    // practice it never is, since both come from the same real product (see the backend property's
    // own remarks), but this stays scoped to its own response rather than assuming that).
    canViewBenchmarking() {
      return this.detailPage.canViewScoringProfiles && this.response.benchmarkingAccess.isOwnedByUser
    },
    benchmarkingDisabledReasonMessage() {
      const key = this.detailPage.benchmarkingDisabledReasonMessageMap[this.response.benchmarkingAccess.disabledReason]
      return this.$t('benchmarking.disabledReasonType.' + key)
    },
    // Same `tab` query param as ProductDetails.vue's own secondaryTabKeys/activeSecondaryTabIndex
    // (see that computed's own comment) — this panel's chip row and the product-level one are never
    // visible at once, so they share one query key without ambiguity. KpiKid is unconditional here
    // (unlike TargetMarket/Sustainability/Benchmarking), matching the unconditional first <b-tab>
    // above.
    secondaryTabKeys() {
      return [
        'KpiKid',
        ...(this.targetMarketSection ? ['TargetMarket'] : []),
        ...(this.sustainabilitySection ? ['Sustainability'] : []),
        ...(this.canViewBenchmarking ? ['Benchmarking'] : []),
      ]
    },
    // User-confirmed: switching investment options keeps this same chip selected (this component
    // instance is mounted once and reused across options — see the class doc comment above — so this
    // needs no extra wiring beyond reading the key fresh against each option's own secondaryTabKeys.
    // Same URL-then-localStorage fallback chain as ProductDetails.vue's own
    // activeSecondaryTabIndex (see its comment for the 'currentSecondaryTabKey' rationale) — shared
    // storage key too, since the two chip rows are never visible at once.
    activeSecondaryTabIndex: {
      get() {
        const fromUrl = this.secondaryTabKeys.indexOf(this.$route.query.tab)
        if (fromUrl !== -1) {
          return fromUrl
        }
        const fromStorage = this.secondaryTabKeys.indexOf(localStorage.getItem('currentSecondaryTabKey'))
        return fromStorage === -1 ? 0 : fromStorage
      },
      set(newIndex) {
        const key = this.secondaryTabKeys[newIndex]
        if (key == null) {
          return
        }
        localStorage.setItem('currentSecondaryTabKey', key)
        this.$router.replace({ query: { ...this.$route.query, tab: key } }).catch(() => {})
      },
    },
  },
  methods: {
    // "Compara col mercato" — per-option KpiKid cross-link, via ProductDetails.vue's pushToRoute
    // (see that method's own comment).
    pushToRoute(routeName) {
      this.detailPage.pushToRoute(routeName, this.detailPage.selectedInvestmentOption.name)
    },
  },
}
</script>

<!-- `.optionName`'s font-size/weight is scoped here (rather than in ProductDetails.vue's styles,
which cannot reach this component's tree) since this class is only ever used inside this one component. -->
<style lang="scss" scoped>
.optionName {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
